import { For, Show, Suspense, createSignal, createMemo, onMount } from "solid-js";
import { A, createAsync, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Card, CardContent, Badge, Skeleton, EmptyState, Input, Select } from "~/components/ui";
import { getVideos } from "~/server/bff";
import { formatRelativeTime, formatDuration } from "~/lib/utils";
import { Search, Video, SortAsc, Filter, FolderPlus, Plus, ChevronDown } from "lucide-solid";

export const route = {
  preload: () => getVideos(),
} satisfies RouteDefinition;

export default function LibraryPage() {
  const [searchQuery, setSearchQuery] = createSignal("");
  const [sortBy, setSortBy] = createSignal("date");
  const [mounted, setMounted] = createSignal(false);

  onMount(() => setMounted(true));

  const videos = createAsync(() => getVideos(), { deferStream: true });

  const filteredVideos = createMemo(() => {
    const list = videos();
    if (!list) return null;
    let vids = list;
    if (searchQuery()) {
      const query = searchQuery().toLowerCase();
      vids = vids.filter(
        (v) =>
          v.title.toLowerCase().includes(query) ||
          v.channel?.name.toLowerCase().includes(query) ||
          v.topicTags.some((t) => t.toLowerCase().includes(query))
      );
    }
    return vids;
  });

  const sortOptions = [
    { value: "date", label: "Date (newest)" },
    { value: "date-asc", label: "Date (oldest)" },
    { value: "channel", label: "Channel" },
    { value: "priority", label: "Priority" },
  ];

  return (
    <AppShell title="Library">
      <div class="p-6">
        <PageHeader
          title="Video Library"
          description="Browse all approved videos"
        />

        <div class="flex items-center gap-4 mb-6">
          <div class="relative flex-1 max-w-md">
            <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search library..."
              class="pl-9"
              value={searchQuery()}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
            />
          </div>
          <Show 
            when={mounted()} 
            fallback={
              <div class="flex h-10 w-40 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                <span class="text-muted-foreground">Sort by</span>
                <ChevronDown class="h-4 w-4 opacity-50" />
              </div>
            }
          >
            <Select
              options={sortOptions}
              value={sortBy()}
              onChange={setSortBy}
              placeholder="Sort by"
              class="w-40"
            />
          </Show>
        </div>

        <Suspense fallback={<LibrarySkeleton />}>
          <Show when={filteredVideos()} fallback={<LibrarySkeleton />}>
            {(items) => (
              <Show
                when={items().length > 0}
                fallback={
                  <EmptyState
                    icon={<Video class="h-12 w-12" />}
                    title="No videos in library"
                    description={searchQuery() ? "No videos match your search" : "Approve videos from your inbox to add them here"}
                  />
                }
              >
                <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <For each={items()}>
                    {(video) => (
                      <Card class="group overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all">
                        <A href={`/video/${video.id}`}>
                          <div class="relative aspect-video">
                            <img
                              src={video.thumbnailUrl}
                              alt={video.title}
                              class="h-full w-full object-cover"
                            />
                            <div class="absolute bottom-2 right-2 rounded bg-black/80 px-1.5 py-0.5 text-xs text-white">
                              {formatDuration(video.duration)}
                            </div>
                            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                        </A>
                        <CardContent class="p-3">
                          <A href={`/video/${video.id}`}>
                            <h3 class="font-medium line-clamp-2 hover:text-primary">{video.title}</h3>
                          </A>
                          <A href={`/explore/sources/${video.channelId}`} class="text-sm text-muted-foreground hover:text-foreground mt-1 block">
                            {video.channel?.name}
                          </A>
                          <p class="text-xs text-muted-foreground mt-1">
                            {formatRelativeTime(video.publishedAt)}
                          </p>
                          <div class="flex flex-wrap gap-1 mt-2">
                            <For each={video.topicTags.slice(0, 2)}>
                              {(tag) => <Badge variant="secondary" class="text-xs">{tag}</Badge>}
                            </For>
                          </div>
                          <div class="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" class="flex-1">
                              <FolderPlus class="h-3 w-3 mr-1" />
                              Dossier
                            </Button>
                            <Button variant="ghost" size="sm" class="flex-1">
                              <Plus class="h-3 w-3 mr-1" />
                              Analysis
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </For>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>
      </div>
    </AppShell>
  );
}

function LibrarySkeleton() {
  return (
    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <For each={[1, 2, 3, 4, 5, 6]}>
        {() => (
          <Card class="overflow-hidden">
            <Skeleton class="aspect-video" />
            <CardContent class="p-3 space-y-2">
              <Skeleton class="h-5 w-full" />
              <Skeleton class="h-4 w-24" />
              <Skeleton class="h-3 w-16" />
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
